import { PageContainer } from '@ant-design/pro-components'
import { useIsFetching, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, Button, Card, Collapse, Skeleton, Tabs } from 'antd'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc.js'
import { ChartNoAxesCombined, Clock3, RefreshCw, Table2 } from 'lucide-react'
import type { ReportDimension } from '@super-mega/server/contracts/analytics'
import { useTRPC } from '@/trpc/react.ts'
import { REPORT_LABELS, REPORT_VIEWS } from './const/reports.ts'
import { ReportCharts } from './report-charts.tsx'
import { ReportFilters } from './report-filters.tsx'
import { ReportMetrics } from './report-metrics.tsx'
import type { ReportSearch } from './report-search.ts'
import { ReportTable, type ReportNavigate } from './report-table.tsx'
import { snapshotTime } from './utils/format.ts'
import { reportQueryOptions, reportScope } from './utils/report-query.ts'

dayjs.extend(utc)

function periodLabel(range: { start: string; end: string }) {
  const startDate = dayjs.utc(range.start).utcOffset(8).format('YYYY-MM-DD')
  const endDate = dayjs.utc(range.end).subtract(1, 'millisecond').utcOffset(8).format('YYYY-MM-DD')
  return `${startDate} 至 ${endDate}`
}

export function ReportPage({
  dimension,
  overview,
  search,
  navigate
}: {
  dimension: ReportDimension
  overview?: boolean
  search: ReportSearch
  navigate: ReportNavigate
}) {
  const trpc = useTRPC()
  const client = useQueryClient()
  const scope = reportScope(search, dimension)
  const view = search.view ?? 'charts'
  const query = useQuery({
    ...trpc.analytics.summary.queryOptions(scope),
    ...reportQueryOptions,
    enabled: view === 'charts'
  })
  const summaryFetching = useIsFetching(trpc.analytics.summary.queryFilter(scope))
  const listFetching = useIsFetching(trpc.analytics.list.queryFilter(scope))
  const loading = summaryFetching + listFetching > 0
  const data = query.data
  async function refresh() {
    await Promise.all([
      client.invalidateQueries(trpc.analytics.summary.queryFilter(scope)),
      client.invalidateQueries(trpc.analytics.list.queryFilter(scope))
    ])
  }
  return (
    <PageContainer
      title={overview ? '经营总览' : `${REPORT_LABELS[dimension]}分析`}
      breadcrumbRender={false}
      content={
        overview ? '结合主体收入、支出和收支结余，查询不同期间的经营表现。' : REPORT_VIEWS[dimension].description
      }
      extra={
        <Button onClick={() => void refresh()} loading={loading} icon={<RefreshCw className="size-4" aria-hidden />}>
          刷新报表
        </Button>
      }
    >
      <div className="flex flex-col gap-5">
        <ReportFilters
          key={JSON.stringify(scope)}
          search={search}
          dimension={dimension}
          navigate={navigate}
          refresh={refresh}
          loading={loading}
        />
        {search.id && (
          <Alert
            type="info"
            showIcon
            title={`正在分析${REPORT_LABELS[dimension]} ID：${search.id}，同时保留组合筛选条件。`}
            action={
              <Button onClick={() => void navigate({ search: { ...search, id: undefined, current: 1 } })}>
                取消单项分析
              </Button>
            }
          />
        )}
        <Tabs
          activeKey={view}
          destroyOnHidden
          onChange={(key) => void navigate({ search: { ...search, view: key === 'table' ? 'table' : 'charts' } })}
          items={[
            {
              key: 'charts',
              label: '图表分析',
              icon: <ChartNoAxesCombined className="size-4" aria-hidden />,
              children: (
                <div className="flex flex-col gap-5">
                  {query.isError ? (
                    <Alert
                      type="error"
                      showIcon
                      title="报表加载失败"
                      description={query.error.message}
                      action={<Button onClick={() => void refresh()}>重试</Button>}
                    />
                  ) : !data ? (
                    <Card>
                      <Skeleton active paragraph={{ rows: 8 }} />
                    </Card>
                  ) : (
                    <>
                      <div className="text-content-muted flex flex-wrap items-center justify-between gap-3 text-xs">
                        <span className="text-content inline-flex flex-wrap items-center gap-2 font-medium">
                          <span className="bg-accent size-1.5 rounded-full" aria-hidden />
                          {periodLabel(data.periods.current)} · 北京时间 · {search.id ? '单项分析' : '按当前条件统计'}
                        </span>
                        <span className="inline-flex items-center gap-2">
                          <Clock3 className="size-3.5" aria-hidden />
                          查询于 {snapshotTime(data.generatedAt)}
                        </span>
                      </div>
                      <ReportMetrics data={data} dimension={dimension} overview={overview} />
                      <div className="text-content-muted text-xs">
                        {search.dateFrom ? '环比为前一个等长期间' : '环比为上月同期'}：
                        {periodLabel(data.periods.previous)} · 同比：{periodLabel(data.periods.year)}。当前期间统计截至{' '}
                        {snapshotTime(data.through)}。
                        <p className="mt-2 mb-0">
                          增长率 =（本期 − 对比期）÷ 对比期 × 100%。对比值为 0 时无法计算百分比；两期都为 0
                          则持平。点击指标下的同比、环比可查看两期数值。
                        </p>
                      </div>
                      <ReportCharts data={data} search={search} dimension={dimension} overview={overview} />
                    </>
                  )}
                </div>
              )
            },
            {
              key: 'table',
              label: '数据明细',
              icon: <Table2 className="size-4" aria-hidden />,
              children: (
                <ReportTable
                  key={`${overview ? 'overview' : dimension}`}
                  dimension={dimension}
                  search={search}
                  navigate={navigate}
                />
              )
            }
          ]}
        />
        <Collapse
          items={[
            {
              key: 'definitions',
              label: '统计口径与查询范围',
              children: (
                <div className="text-content-muted space-y-2 text-sm">
                  <p>
                    订单按创建时间计数，合并租赁与服务订单，排除已取消订单；下单客户、服务对象和各维度下单数量按期间订单去重，归档订单保留。没有归属的记录在明细中归入“未归属”。
                  </p>
                  <p>
                    应收按出账时间统计原始金额，并计入期间生效的调整；排除草稿、作废及应付账单。收退款仅计已确认流水，按实际发生时间统计，通过账单分配归属维度。实际退款包含全额和部分退款；待确认、作废流水不计入。净收款
                    = 实际收款 −
                    实际退款，跨期退款计入退款发生当期，净收款可能为负。费用减免属于应收调整，不作为实际退款。期间账单未收是本次查询时的剩余应收，不是历史期末余额。
                  </p>
                  <p>
                    支出台账保存后按应付日期计入支出，编辑或删除后同步更新统计。收支结余 = 净收款 − 支出金额，
                    用于台账收支对比，不代表已实际付款或会计净利润。历史未转换的应付账单仍按出账及调整生效时间统计。
                    主体支出不分摊给客户、场地或业务员，选择这些归属筛选时不包含主体公共支出。
                  </p>
                  <p>
                    月份查询按上月及去年同月的相同进度比较；自定义区间与前一个等长期间、去年同日期区间比较。近十二个月趋势保留当前组合维度筛选，用作历史背景，日期窗口与期间趋势不同。
                  </p>
                  <p>
                    期间在用租约按租期重叠计算，不等同于房间入住率。地址筛选作用于订单或账单的场地归属；服务订单没有地址字段，所以选择具体地址时不计服务订单数量。财务归属以账单保存的主体、客户、业务员、业务和地址为准。
                  </p>
                  <p>
                    图表分析与数据明细分别加载；修改业务后点击“刷新报表”即可重新查询。明细中的关键词 / ID
                    只筛选表格（名称模糊匹配或 ID 精确匹配），组合筛选与 ID
                    单项分析作用于全部指标和图表。报表沿用财务查看权限，展示当前实例范围；目前未采集浏览行为，不显示浏览量。
                  </p>
                </div>
              )
            }
          ]}
        />
      </div>
    </PageContainer>
  )
}
